import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Seo from "@/components/seo/Seo";
import { MarketplaceItem, marketplaceCategories, Restaurant, restaurantCategories, RestaurantMenu, priceRanges } from "@shared/schema";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Search, MapPin, Tag, ExternalLink, Star, Plus, Phone, Clock, Utensils, UtensilsCrossed, Mail, Bookmark } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { PageContainer } from "@/components/layout/PageContainer";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { normalizeImageUrl } from "@/lib/image-url";

// 핫샵&딜 상품 카드의 찜하기 버튼. itemType: "product"는
// functions/api/bookmarks/index.ts의 VALID_ITEM_TYPES에 포함되어 있고
// MyPage.tsx의 "상품" 탭이 이 버튼으로 생성된 북마크를 표시한다.
function ProductBookmarkButton({ item }: { item: MarketplaceItem }) {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();

  const { data: bookmarkData } = useQuery<{ isBookmarked: boolean; bookmarkId?: string }>({
    queryKey: ["/api/bookmarks/check", user?.uid, "product", item.id],
    queryFn: async () => {
      const params = new URLSearchParams({
        userId: user?.uid || "",
        itemType: "product",
        itemId: String(item.id ?? ""),
      });
      const res = await fetch(`/api/bookmarks/check?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to check bookmark");
      return res.json();
    },
    enabled: isAuthenticated && !!user,
  });
  const isBookmarked = bookmarkData?.isBookmarked || false;

  const addBookmarkMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("로그인이 필요합니다");
      return await apiRequest("POST", "/api/bookmarks", {
        userId: user.uid,
        itemType: "product",
        itemId: item.id,
        itemTitle: item.titleKo || item.title,
        itemImageUrl: normalizeImageUrl(item.imageUrl) || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bookmarks/check", user?.uid, "product", item.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/bookmarks", user?.uid] });
      toast({ title: "찜하기 완료", description: "마이페이지에서 확인하실 수 있습니다" });
    },
    onError: (error: any) => {
      toast({ title: "찜하기 실패", description: error.message, variant: "destructive" });
    },
  });

  const removeBookmarkMutation = useMutation({
    mutationFn: async () => {
      if (!bookmarkData?.bookmarkId) throw new Error("북마크 ID를 찾을 수 없습니다");
      return await apiRequest("DELETE", `/api/bookmarks/${bookmarkData.bookmarkId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bookmarks/check", user?.uid, "product", item.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/bookmarks", user?.uid] });
      toast({ title: "찜하기 취소", description: "찜하기가 해제되었습니다" });
    },
    onError: (error: any) => {
      toast({ title: "찜하기 취소 실패", description: error.message, variant: "destructive" });
    },
  });

  if (!isAuthenticated) return null;

  const isPending = addBookmarkMutation.isPending || removeBookmarkMutation.isPending;

  return (
    <Button
      variant="ghost"
      size="icon"
      className={`h-7 w-7 shrink-0 ${isBookmarked ? "text-primary" : "text-muted-foreground"}`}
      disabled={isPending}
      data-testid={`button-bookmark-product-${item.id}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isBookmarked) {
          removeBookmarkMutation.mutate();
        } else {
          addBookmarkMutation.mutate();
        }
      }}
    >
      <Bookmark className={`h-4 w-4 ${isBookmarked ? "fill-current" : ""}`} />
    </Button>
  );
}

const regions = [
  { value: "ALL", label: "전체 지역" },
  { value: "NSW", label: "뉴사우스웨일스" },
  { value: "VIC", label: "빅토리아" },
  { value: "QLD", label: "퀸즐랜드" },
  { value: "SA", label: "남호주" },
  { value: "WA", label: "서호주" },
  { value: "TAS", label: "태즈매니아" },
  { value: "NT", label: "노던 테리토리" },
  { value: "ACT", label: "수도 준주" },
];

export default function Marketplace() {
  const { user, membershipLevel, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedRegion, setSelectedRegion] = useState("ALL");
  const [activeTab, setActiveTab] = useState("hotdeals");
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);

  // Fetch marketplace items
  const { data: items = [], isLoading: itemsLoading } = useQuery<MarketplaceItem[]>({
    queryKey: ["/api/marketplace/items", selectedRegion],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedRegion !== "ALL") {
        params.set("region", selectedRegion);
      }
      const response = await fetch(`/api/marketplace/items?${params}`);
      if (!response.ok) throw new Error("Failed to fetch items");
      return response.json();
    },
  });

  // Fetch restaurants
  const { data: restaurants = [], isLoading: restaurantsLoading } = useQuery<Restaurant[]>({
    queryKey: ["/api/restaurants", selectedRegion],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedRegion !== "ALL") {
        params.set("region", selectedRegion);
      }
      const response = await fetch(`/api/restaurants?${params}`);
      if (!response.ok) throw new Error("Failed to fetch restaurants");
      return response.json();
    },
  });

  // Fetch restaurant menus when a restaurant is selected
  const { data: restaurantMenus = [] } = useQuery<RestaurantMenu[]>({
    queryKey: ["/api/restaurants", selectedRestaurant?.id, "menus"],
    enabled: !!selectedRestaurant,
    queryFn: async () => {
      if (!selectedRestaurant) return [];
      const response = await fetch(`/api/restaurants/${selectedRestaurant.id}/menus`);
      if (!response.ok) throw new Error("Failed to fetch menus");
      return response.json();
    },
  });

  // Filter items based on search term, category, and membership
  const filteredItems = items.filter((item: MarketplaceItem) => {
    if (item.status !== "active") return false;

    const matchesSearch = !searchTerm ||
      item.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.titleKo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = selectedCategory === "all" || item.category === selectedCategory;

    const hasAccess =
      (!item.isPlatinumOnly && !item.isPremiumOnly) ||
      (item.isPremiumOnly && (membershipLevel === "premium" || membershipLevel === "platinum")) ||
      (item.isPlatinumOnly && membershipLevel === "platinum");

    return matchesSearch && matchesCategory && hasAccess;
  });

  // Filter restaurants
  const filteredRestaurants = restaurants.filter((restaurant: Restaurant) => {
    if (restaurant.status !== "active") return false;

    const matchesSearch = !searchTerm ||
      restaurant.restaurantName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      restaurant.restaurantNameKo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      restaurant.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = selectedCategory === "all" || restaurant.category === selectedCategory;

    const hasAccess =
      (!restaurant.isPlatinumOnly && !restaurant.isPremiumOnly) ||
      (restaurant.isPremiumOnly && (membershipLevel === "premium" || membershipLevel === "platinum")) ||
      (restaurant.isPlatinumOnly && membershipLevel === "platinum");

    return matchesSearch && matchesCategory && hasAccess;
  });

  // Sort items
  const sortedItems = [...filteredItems].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return (b.priority || 0) - (a.priority || 0);
  });

  // Sort restaurants
  const sortedRestaurants = [...filteredRestaurants].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return (b.priority || 0) - (a.priority || 0);
  });

  const getCategoryLabel = (categoryId: string) => {
    return marketplaceCategories.find(c => c.id === categoryId)?.label || categoryId;
  };

  const getRestaurantCategoryLabel = (categoryId: string) => {
    return restaurantCategories.find(c => c.id === categoryId)?.label || categoryId;
  };

  const getDiscountPercentage = (item: MarketplaceItem) => {
    if (!item.originalPrice || !item.salePrice) return null;
    const original = Number(item.originalPrice);
    const sale = Number(item.salePrice);
    if (original <= sale) return null;
    return Math.round(((original - sale) / original) * 100);
  };

  const getPriceRangeLabel = (priceRange: string) => {
    return priceRanges.find(p => p.id === priceRange)?.label || "$$";
  };

  const isLoading = activeTab === "hotdeals" ? itemsLoading : restaurantsLoading;
  const hasResults = activeTab === "hotdeals" ? sortedItems.length > 0 : sortedRestaurants.length > 0;

  return (
    <>
      <Seo
        title="핫샵&딜&맛집 — OZKNEWS"
        description="호주 한인 커뮤니티 특가·할인 정보와 맛집 소개. 교민 업체 추천 맛집, 핫딜, 이벤트를 한곳에서."
        path="/marketplace"
        keywords={["핫딜", "할인", "맛집", "특가", "교민 업체", "레스토랑"]}
      />

      <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary/10 via-background to-background border-b">
        <div className="container mx-auto px-4 py-12">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold mb-3">핫샵&딜&맛집</h1>
              <p className="text-xl text-muted-foreground">
                호주 한인 커뮤니티의 특가 정보와 맛집 소개
              </p>
            </div>
            {isAuthenticated && (
              <Button
                onClick={() => setLocation(activeTab === "hotdeals" ? "/marketplace/register" : "#")}
                data-testid="button-register-item"
              >
                <Plus className="h-4 w-4 mr-2" />
                {activeTab === "hotdeals" ? "상품 등록" : "맛집 등록"}
              </Button>
            )}
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="hotdeals" data-testid="tab-hotdeals">
                <Tag className="h-4 w-4 mr-2" />
                핫샵&딜
              </TabsTrigger>
              <TabsTrigger value="restaurants" data-testid="tab-restaurants">
                <Utensils className="h-4 w-4 mr-2" />
                맛집
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Search and Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">

            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger data-testid="select-category">
                <Tag className="h-4 w-4 mr-2" />
                <SelectValue placeholder="카테고리" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">전체 카테고리</SelectItem>
                {activeTab === "hotdeals"
                  ? marketplaceCategories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.icon} {cat.label}
                      </SelectItem>
                    ))
                  : restaurantCategories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.icon} {cat.label}
                      </SelectItem>
                    ))
                }
              </SelectContent>
            </Select>

            <Select value={selectedRegion} onValueChange={setSelectedRegion}>
              <SelectTrigger data-testid="select-region">
                <MapPin className="h-4 w-4 mr-2" />
                <SelectValue placeholder="지역" />
              </SelectTrigger>
              <SelectContent>
                {regions.map((region) => (
                  <SelectItem key={region.value} value={region.value}>
                    {region.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {isLoading ? (
          <div className="text-center py-12 text-muted-foreground">
            로딩 중...
          </div>
        ) : !hasResults ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg mb-2">
              검색 결과가 없습니다
            </p>
            <p className="text-sm text-muted-foreground">
              다른 검색어나 필터를 시도해보세요
            </p>
          </div>
        ) : (
          <>
            {activeTab === "hotdeals" && (
              <>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-muted-foreground">
                    총 <span className="font-semibold text-foreground">{sortedItems.length}</span>개 상품
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sortedItems.map((item) => {
                    const discount = getDiscountPercentage(item);
                    return (
                      <Card
                        key={item.id}
                        className="overflow-hidden hover-elevate transition-all"
                        data-testid={`card-item-${item.id}`}
                      >
                        <div className="relative bg-muted">
                          <img
                            src={item.imageUrl || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%23e5e7eb" width="800" height="600"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="32" dy="310" dx="400" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E'}
                            alt={item.titleKo || item.title}
                            className="w-full h-48 object-cover"
                            onError={(e) => {
                              e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%23e5e7eb" width="800" height="600"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="32" dy="310" dx="400" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                            }}
                          />
                          {item.featured && (
                            <Badge className="absolute top-2 left-2 bg-primary text-primary-foreground">
                              <Star className="h-3 w-3 mr-1 fill-current" />
                              추천
                            </Badge>
                          )}
                          {discount && (
                            <Badge className="absolute top-2 right-2 bg-destructive text-destructive-foreground">
                              {discount}% OFF
                            </Badge>
                          )}
                          {(item.isPremiumOnly || item.isPlatinumOnly) && (
                            <Badge
                              variant="secondary"
                              className="absolute bottom-2 right-2 bg-background/90 backdrop-blur"
                            >
                              {item.isPlatinumOnly ? "플래티넘 전용" : "프리미엄 전용"}
                            </Badge>
                          )}
                        </div>

                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <p className="text-sm text-muted-foreground mb-1">
                                {item.businessName}
                              </p>
                              <h3 className="font-semibold text-lg leading-tight">
                                {item.titleKo || item.title}
                              </h3>
                            </div>
                            <ProductBookmarkButton item={item} />
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">
                              {getCategoryLabel(item.category)}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              <MapPin className="h-3 w-3 mr-1" />
                              {regions.find(r => r.value === item.region)?.label}
                            </Badge>
                          </div>
                        </CardHeader>

                        <CardContent className="pb-3">
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {item.descriptionKo || item.description}
                          </p>

                          <div className="flex items-baseline gap-2 mt-4">
                            <span className="text-2xl font-bold text-primary" data-testid={`price-sale-${item.id}`}>
                              ${Number(item.salePrice).toFixed(2)} AUD
                            </span>
                            {item.originalPrice && Number(item.originalPrice) > Number(item.salePrice) && (
                              <span
                                className="text-sm text-muted-foreground line-through decoration-2"
                                style={{ textDecoration: 'line-through' }}
                                data-testid={`price-original-${item.id}`}
                              >
                                ${Number(item.originalPrice).toFixed(2)} AUD
                              </span>
                            )}
                          </div>
                        </CardContent>

                        <CardFooter className="pt-0 flex gap-2">
                          <Link href={`/marketplace/${item.id}`} className="flex-1">
                            <Button className="w-full" data-testid={`button-view-${item.id}`}>
                              자세히 보기
                            </Button>
                          </Link>
                          {item.website && (
                            <Button
                              variant="outline"
                              size="icon"
                              asChild
                              data-testid={`button-website-${item.id}`}
                            >
                              <a href={item.website} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </Button>
                          )}
                        </CardFooter>
                      </Card>
                    );
                  })}
                </div>
              </>
            )}

            {activeTab === "restaurants" && (
              <>
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Sidebar - Categories */}
                <aside className="lg:col-span-1">
                  <Card className="p-4 sticky top-24">
                    <h2 className="font-semibold mb-4">카테고리</h2>
                    <RadioGroup
                      value={selectedCategory}
                      onValueChange={setSelectedCategory}
                      className="space-y-1"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="all" id="restaurant-category-all" />
                        <Label htmlFor="restaurant-category-all" className="cursor-pointer text-sm font-normal leading-tight">
                          전체
                        </Label>
                      </div>
                      {restaurantCategories.map((category) => (
                        <div key={category.id} className="flex items-center space-x-2">
                          <RadioGroupItem value={category.id} id={`restaurant-category-${category.id}`} />
                          <Label htmlFor={`restaurant-category-${category.id}`} className="cursor-pointer text-sm font-normal leading-tight">
                            {category.icon} {category.label}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </Card>
                </aside>

                {/* Main Content */}
                <main className="lg:col-span-3">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-muted-foreground">
                      총 <span className="font-semibold text-foreground">{sortedRestaurants.length}</span>개 맛집
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {sortedRestaurants.map((restaurant) => {
                    const avgRating = Number(restaurant.averageRating) || 0;
                    return (
                      <Card
                        key={restaurant.id}
                        className="overflow-hidden hover-elevate transition-all"
                        data-testid={`card-restaurant-${restaurant.id}`}
                      >
                        <div className="relative bg-muted">
                          <img
                            src={restaurant.coverImageUrl || restaurant.logoUrl || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%23e5e7eb" width="800" height="600"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="32" dy="310" dx="400" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E'}
                            alt={restaurant.restaurantNameKo || restaurant.restaurantName}
                            className="w-full h-48 object-cover"
                            onError={(e) => {
                              e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%23e5e7eb" width="800" height="600"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="32" dy="310" dx="400" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                            }}
                          />
                          {restaurant.featured && (
                            <Badge className="absolute top-2 left-2 bg-primary text-primary-foreground">
                              <Star className="h-3 w-3 mr-1 fill-current" />
                              추천
                            </Badge>
                          )}
                          <Badge className="absolute top-2 right-2 bg-background/90 backdrop-blur">
                            {getPriceRangeLabel(restaurant.priceRange || "moderate")}
                          </Badge>
                          {(restaurant.isPremiumOnly || restaurant.isPlatinumOnly) && (
                            <Badge
                              variant="secondary"
                              className="absolute bottom-2 right-2 bg-background/90 backdrop-blur"
                            >
                              {restaurant.isPlatinumOnly ? "플래티넘 전용" : "프리미엄 전용"}
                            </Badge>
                          )}
                        </div>

                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <h3 className="font-semibold text-lg leading-tight">
                                {restaurant.restaurantNameKo || restaurant.restaurantName}
                              </h3>
                              <p className="text-sm text-muted-foreground mt-1">
                                {restaurant.restaurantName}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">
                              {getRestaurantCategoryLabel(restaurant.category)}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              <MapPin className="h-3 w-3 mr-1" />
                              {regions.find(r => r.value === restaurant.region)?.label}
                            </Badge>
                          </div>
                          {avgRating > 0 && (
                            <div className="flex items-center gap-1 mt-2">
                              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                              <span className="text-sm font-semibold">{avgRating.toFixed(1)}</span>
                              <span className="text-xs text-muted-foreground">
                                ({restaurant.reviewCount || 0})
                              </span>
                            </div>
                          )}
                        </CardHeader>

                        <CardContent className="pb-3">
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {restaurant.descriptionKo || restaurant.description || restaurant.shortDescription}
                          </p>

                          {restaurant.specialties && restaurant.specialties.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-3">
                              {restaurant.specialties.slice(0, 3).map((specialty: string, idx: number) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {specialty}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </CardContent>

                        <CardFooter className="pt-0 flex gap-2">
                          <Button
                            className="flex-1"
                            onClick={() => setSelectedRestaurant(restaurant)}
                            data-testid={`button-view-restaurant-${restaurant.id}`}
                          >
                            메뉴 보기
                          </Button>
                          {restaurant.contactPhone && (
                            <Button
                              variant="outline"
                              size="icon"
                              asChild
                              data-testid={`button-phone-${restaurant.id}`}
                            >
                              <a href={`tel:${restaurant.contactPhone}`}>
                                <Phone className="h-4 w-4" />
                              </a>
                            </Button>
                          )}
                        </CardFooter>
                      </Card>
                    );
                  })}
                  </div>
                </main>
              </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Restaurant Menu Dialog */}
      <Dialog open={!!selectedRestaurant} onOpenChange={(open) => !open && setSelectedRestaurant(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          {selectedRestaurant && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">
                  {selectedRestaurant.restaurantNameKo || selectedRestaurant.restaurantName}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                {/* Restaurant Info */}
                <div className="flex items-start gap-4">
                  {(selectedRestaurant.logoUrl || selectedRestaurant.coverImageUrl) && (
                    <img
                      src={selectedRestaurant.logoUrl || selectedRestaurant.coverImageUrl || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23e5e7eb" width="200" height="200"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="16" dy="105" dx="100" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E'}
                      alt={selectedRestaurant.restaurantNameKo || selectedRestaurant.restaurantName}
                      className="w-24 h-24 rounded-lg object-cover"
                      onError={(e) => {
                        e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23e5e7eb" width="200" height="200"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="16" dy="105" dx="100" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                      }}
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline">
                        {getRestaurantCategoryLabel(selectedRestaurant.category)}
                      </Badge>
                      <Badge variant="secondary">
                        {priceRanges.find(p => p.id === selectedRestaurant.priceRange)?.label || selectedRestaurant.priceRange}
                      </Badge>
                      {selectedRestaurant.averageRating && parseFloat(selectedRestaurant.averageRating) > 0 && (
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                          <span className="text-sm font-semibold">{parseFloat(selectedRestaurant.averageRating).toFixed(1)}</span>
                          <span className="text-xs text-muted-foreground">({selectedRestaurant.reviewCount})</span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {selectedRestaurant.descriptionKo || selectedRestaurant.description || selectedRestaurant.shortDescription}
                    </p>
                    {selectedRestaurant.address && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        <span>{selectedRestaurant.address}, {selectedRestaurant.city}, {selectedRestaurant.state}</span>
                      </div>
                    )}
                    {selectedRestaurant.businessHours && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                        <Clock className="h-4 w-4" />
                        <span>{selectedRestaurant.businessHours}</span>
                      </div>
                    )}
                    {selectedRestaurant.contactPhone && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                        <Phone className="h-4 w-4" />
                        <span>{selectedRestaurant.contactPhone}</span>
                      </div>
                    )}
                    {selectedRestaurant.contactEmail && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                        <Mail className="h-4 w-4" />
                        <a href={`mailto:${selectedRestaurant.contactEmail}`} className="hover:underline">
                          {selectedRestaurant.contactEmail}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Menu Section */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <UtensilsCrossed className="h-5 w-5" />
                    <h3 className="text-xl font-semibold">메뉴</h3>
                  </div>

                  {restaurantMenus.length === 0 ? (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <Utensils className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                        <p className="text-muted-foreground">등록된 메뉴가 없습니다</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-6">
                      {/* Group menus by category */}
                      {['main', 'side', 'drink', 'dessert', 'set'].map(category => {
                        const categoryMenus = restaurantMenus.filter(m => m.category === category && m.isAvailable);
                        if (categoryMenus.length === 0) return null;

                        const categoryLabels: Record<string, string> = {
                          main: '메인 메뉴',
                          side: '사이드 메뉴',
                          drink: '음료',
                          dessert: '디저트',
                          set: '세트 메뉴'
                        };

                        return (
                          <div key={category}>
                            <h4 className="font-semibold text-lg mb-3">{categoryLabels[category]}</h4>
                            <div className="grid gap-3">
                              {categoryMenus.map((menu) => (
                                <Card key={menu.id} className="hover-elevate">
                                  <CardContent className="p-4">
                                    <div className="flex items-start justify-between gap-4">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          <h5 className="font-semibold">
                                            {menu.menuNameKo || menu.menuName}
                                          </h5>
                                          {menu.spicyLevel && menu.spicyLevel > 0 && (
                                            <Badge variant="destructive" className="text-xs">매운맛</Badge>
                                          )}
                                          {menu.isVegetarian && (
                                            <Badge variant="outline" className="text-xs">채식</Badge>
                                          )}
                                        </div>
                                        {menu.menuName && menu.menuNameKo && (
                                          <p className="text-sm text-muted-foreground">{menu.menuName}</p>
                                        )}
                                        {menu.description && (
                                          <p className="text-sm text-muted-foreground mt-1">{menu.description}</p>
                                        )}
                                      </div>
                                      <div className="text-right">
                                        <p className="font-semibold text-lg">${menu.price}</p>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Contact Buttons */}
                <div className="flex gap-2 pt-4 border-t">
                  {selectedRestaurant.contactPhone && (
                    <Button className="flex-1" asChild>
                      <a href={`tel:${selectedRestaurant.contactPhone}`}>
                        <Phone className="h-4 w-4 mr-2" />
                        전화하기
                      </a>
                    </Button>
                  )}
                  {selectedRestaurant.website && (
                    <Button variant="outline" className="flex-1" asChild>
                      <a href={selectedRestaurant.website} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        웹사이트
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
}
